"use client";

import React from "react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { ShieldCheck, Lock, FileText, ArrowRight, Layers3 } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { cn } from "@/lib/utils";
const operationalBoundaries = [{
  icon: ShieldCheck,
  title: "Governed content lifecycle",
  description: "Content moves through Draft → Review → Approved. EloRAG only answers from Approved content."
}, {
  icon: Lock,
  title: "Strict tenant isolation",
  description: "Multi-tenant boundaries help ensure data stays separated across environments and delivery contexts."
}, {
  icon: FileText,
  title: "Audit visibility",
  description: "Queries, citations, and access behavior remain traceable for stronger operational oversight."
}];
export default function EnterpriseGradeGovernance() {
  const {
    ref: textRef,
    isVisible: isTextVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: visualRef,
    isVisible: isVisualVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="enterprise-grade-governance" className="bg-background relative overflow-hidden py-16 lg:py-20 border-b border-border">
      <div className="absolute inset-0 pointer-events-none opacity-[0.03] dark:opacity-[0.05]" style={{
      backgroundImage: "radial-gradient(circle at 2px 2px, var(--foreground) 1px, transparent 0)",
      backgroundSize: "32px 32px"
    }} />

      <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <div ref={textRef} className={cn("flex flex-col gap-8 transition-all duration-1000 ease-out", isTextVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8")}>
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 text-primary text-sm font-medium mb-6 border border-primary/20">
                <Layers3 className="size-4" />
                <span>Security & governance</span>
              </div>

              <h1 className="text-4xl sm:text-5xl lg:text-[3.6rem] font-bold text-foreground tracking-tight mb-6 leading-[1.05]">
                Built for control, not generic AI behavior
              </h1>

              <p className="text-lg text-muted-foreground max-w-xl leading-relaxed">
                EloRAG is designed for controlled RAG delivery. With approved-only retrieval,
                tenant-aware boundaries, audit visibility, and citation-based answers,
                the system stays aligned with governed business knowledge instead of broad AI guesswork.
              </p>
            </div>

            <div className="flex flex-col gap-6">
              {operationalBoundaries.map((boundary, i) => <div key={i} data-index={i} className="flex gap-4 items-start">
                  <div className="mt-1 bg-secondary p-2 rounded-md border border-border shrink-0">
                    <boundary.icon className="size-5 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-foreground mb-1">{boundary.title}</h3>
                    <p className="text-sm text-muted-foreground leading-relaxed">{boundary.description}</p>
                  </div>
                </div>)}
            </div>

            <div className="flex flex-wrap items-center gap-4 pt-2">
              <Button size="lg" asChild>
                <Link to="/BookADemoContactSalesPage">
                  Book a Demo
                  <ArrowRight className="ml-2 size-4" />
                </Link>
              </Button>

              <Button size="lg" variant="outline" asChild>
                <Link to="/PricingPage">
                  View Pricing & Caps
                </Link>
              </Button>
            </div>
          </div>

          <div ref={visualRef} className={cn("relative transition-all duration-1000 delay-200 ease-out", isVisualVisible ? "opacity-100 translate-x-0" : "opacity-0 translate-x-8")}>
            <div className="relative aspect-square max-w-md mx-auto lg:max-w-none">
              <div className="absolute inset-0 bg-gradient-to-tr from-primary/5 to-transparent rounded-3xl border border-border/50 shadow-2xl" />
              <div className="absolute inset-4 bg-card rounded-2xl border border-border shadow-sm flex items-center justify-center overflow-hidden">
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1588799/img/elorag_shield_digital_blocks_3d.png" alt="3D shield visual representing governed AI boundaries and control" className="w-full h-full object-cover opacity-90 hover:opacity-100 transition-opacity duration-500" />

                <div className="absolute top-6 left-6 bg-background/90 backdrop-blur-sm border border-border rounded-md p-3 shadow-sm flex items-center gap-3">
                  <div className="size-2 rounded-full bg-emerald-500 animate-pulse" />
                  <span className="text-xs font-mono text-foreground font-medium">Tenant Isolated</span>
                </div>

                <div className="absolute bottom-6 right-6 bg-background/90 backdrop-blur-sm border border-border rounded-md p-3 shadow-sm flex items-center gap-3">
                  <Lock className="size-4 text-primary" />
                  <span className="text-xs font-mono text-foreground font-medium">Approved-Only Retrieval</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}